"use client"

import { useState, useCallback, useMemo, useEffect } from 'react'
import type { ReactNode } from 'react'

type FaviconProps = {
  domain: string
  size?: number
  className?: string
  fallbackClassName?: string
  /** Optional custom fallback to render instead of the default globe icon */
  renderFallback?: () => ReactNode
}

/** Short / tracking hosts that should resolve to a brand apex for icon lookup */
const DOMAIN_ALIASES: Record<string, string> = {
  't.co': 'x.com',
  'twitter.com': 'x.com',
  'mobile.twitter.com': 'x.com',
  'www.twitter.com': 'x.com',
  'l.instagram.com': 'instagram.com',
  'www.instagram.com': 'instagram.com',
  'm.facebook.com': 'facebook.com',
  'lm.facebook.com': 'facebook.com',
  'l.facebook.com': 'facebook.com',
  'www.facebook.com': 'facebook.com',
  'm.youtube.com': 'youtube.com',
  'youtu.be': 'youtube.com',
  'www.youtube.com': 'youtube.com',
}

/** Common referral / mobile subdomains that rarely have their own favicon */
const STRIP_SUBDOMAINS = new Set([
  'www',
  'm',
  'l',
  'lm',
  'mobile',
  'amp',
  'web',
  'touch',
  'i',
  'go',
])

/**
 * Multi-part public suffixes we care about for apex extraction.
 * Intentionally small — avoids pulling a full PSL for favicon lookup.
 */
const MULTI_PART_TLDS = new Set([
  'co.uk',
  'org.uk',
  'ac.uk',
  'gov.uk',
  'com.au',
  'net.au',
  'org.au',
  'co.nz',
  'co.jp',
  'com.br',
  'com.mx',
  'co.in',
  'com.tw',
  'com.sg',
])

/**
 * Extracts a clean hostname from various formats (URLs, domains with paths, etc.)
 */
function extractDomain(input: string): string {
  if (!input || input === 'Direct' || input === '(direct)') return ''

  let domain = input.trim()

  // Remove protocol if present
  domain = domain.replace(/^https?:\/\//i, '')

  // Remove www. prefix
  domain = domain.replace(/^www\./i, '')

  // Remove path, query, and hash
  domain = domain.split('/')[0].split('?')[0].split('#')[0]

  // Strip credentials / port
  domain = domain.replace(/^[^@]+@/, '').replace(/:\d+$/, '')

  // Basic validation - must have at least one dot for a valid domain
  if (!domain.includes('.')) return ''

  return domain.toLowerCase()
}

/**
 * Resolve the best domain to ask a favicon service for.
 * Keeps labels untouched — only used for the icon URL.
 */
function resolveFaviconDomain(domain: string): string {
  if (!domain) return ''

  if (DOMAIN_ALIASES[domain]) return DOMAIN_ALIASES[domain]

  const parts = domain.split('.')
  if (parts.length >= 3 && STRIP_SUBDOMAINS.has(parts[0])) {
    const stripped = parts.slice(1).join('.')
    return DOMAIN_ALIASES[stripped] || stripped
  }

  return domain
}

/** Apex / registrable-ish domain for a secondary favicon attempt */
function getApexDomain(domain: string): string {
  const parts = domain.split('.')
  if (parts.length <= 2) return domain

  const lastTwo = parts.slice(-2).join('.')
  if (MULTI_PART_TLDS.has(lastTwo) && parts.length >= 3) {
    return parts.slice(-3).join('.')
  }

  return lastTwo
}

function duckDuckGoIconUrl(domain: string): string {
  return `https://icons.duckduckgo.com/ip3/${domain}.ico`
}

function googleIconUrl(domain: string): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=64`
}

/**
 * DuckDuckGo serves a fixed 48×48 PNG placeholder on misses (HTTP 404 body still loads as an image).
 * Without CORS we can't inspect pixels — dimensions are the reliable signal.
 */
function isDuckDuckGoPlaceholder(img: HTMLImageElement): boolean {
  return img.naturalWidth === 48 && img.naturalHeight === 48
}

/**
 * Google's missing-favicon glyph is a tiny 16×16 globe even when sz=64 is requested.
 */
function isGooglePlaceholder(img: HTMLImageElement): boolean {
  return img.naturalWidth <= 16 && img.naturalHeight <= 16
}

function FaviconFallback({ size, className = '' }: { size: number; className?: string }) {
  return (
    <div
      className={`flex items-center justify-center rounded shrink-0 text-muted-foreground/70 ${className}`}
      style={{ width: size, height: size, minWidth: size }}
      aria-hidden
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.5" fill="none" opacity="0.55" />
        <ellipse cx="12" cy="12" rx="4.5" ry="10" stroke="currentColor" strokeWidth="1" fill="none" opacity="0.4" />
        <line x1="2" y1="12" x2="22" y2="12" stroke="currentColor" strokeWidth="1" opacity="0.35" />
      </svg>
    </div>
  )
}

type IconAttempt = {
  url: string
  kind: 'ddg' | 'google'
}

function buildAttempts(domain: string): IconAttempt[] {
  const clean = extractDomain(domain)
  if (!clean) return []

  const primary = resolveFaviconDomain(clean)
  const apex = getApexDomain(primary)
  const urls: IconAttempt[] = [{ url: duckDuckGoIconUrl(primary), kind: 'ddg' }]

  if (apex && apex !== primary) {
    urls.push({ url: duckDuckGoIconUrl(apex), kind: 'ddg' })
  }

  urls.push({ url: googleIconUrl(primary), kind: 'google' })
  if (apex && apex !== primary) {
    urls.push({ url: googleIconUrl(apex), kind: 'google' })
  }

  const seen = new Set<string>()
  return urls.filter((a) => {
    if (seen.has(a.url)) return false
    seen.add(a.url)
    return true
  })
}

/**
 * Favicon component — DuckDuckGo first, Google as backup, muted globe last.
 * Accepts an optional `renderFallback` prop for custom fallback rendering.
 */
export default function Favicon({
  domain,
  size = 16,
  className = '',
  fallbackClassName = '',
  renderFallback,
}: FaviconProps) {
  const attempts = useMemo(() => buildAttempts(domain), [domain])
  const [attemptIndex, setAttemptIndex] = useState(0)
  const [isReady, setIsReady] = useState(false)

  useEffect(() => {
    setAttemptIndex(0)
    setIsReady(false)
  }, [domain])

  const current = attempts[attemptIndex]
  const exhausted = attempts.length === 0 || attemptIndex >= attempts.length

  const advance = useCallback(() => {
    setIsReady(false)
    setAttemptIndex((i) => i + 1)
  }, [])

  const handleLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      const img = e.currentTarget
      const kind = current?.kind
      if (!kind) return

      if (kind === 'ddg' && isDuckDuckGoPlaceholder(img)) {
        advance()
        return
      }
      if (kind === 'google' && isGooglePlaceholder(img)) {
        advance()
        return
      }

      setIsReady(true)
    },
    [advance, current?.kind],
  )

  const fallbackNode = renderFallback ? (
    <>{renderFallback()}</>
  ) : (
    <FaviconFallback size={size} className={fallbackClassName} />
  )

  if (exhausted) {
    return fallbackNode
  }

  return (
    <span
      className="relative inline-flex shrink-0 items-center justify-center"
      style={{ width: size, height: size, minWidth: size }}
    >
      {!isReady && fallbackNode}
      <img
        key={current.url}
        src={current.url}
        alt=""
        width={size}
        height={size}
        className={`rounded shrink-0 ${className}`}
        style={{
          width: size,
          height: size,
          minWidth: size,
          minHeight: size,
          position: isReady ? 'static' : 'absolute',
          inset: 0,
          opacity: isReady ? 1 : 0,
          pointerEvents: 'none',
        }}
        onError={advance}
        onLoad={handleLoad}
        loading="lazy"
        decoding="async"
        referrerPolicy="no-referrer"
      />
    </span>
  )
}

/**
 * Helper function to check if a label looks like a domain/URL
 */
export function isDomainLike(label: string): boolean {
  if (!label) return false

  // Skip known non-domain values
  const nonDomains = [
    'Direct',
    '(direct)',
    'Email',
    'Social',
    'Organic Search',
    'Paid Search',
    'Referral',
    'Display',
  ]
  if (nonDomains.includes(label)) return false

  // Check if it looks like a domain (has a dot and valid TLD pattern)
  const cleaned = label.replace(/^https?:\/\//i, '').replace(/^www\./i, '').split('/')[0]
  return cleaned.includes('.') && /\.[a-z]{2,}$/i.test(cleaned)
}
